import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import {
  ArrowUpRight,
  CircleCheck,
  ClipboardList,
  FileText,
  FlaskConical,
  MessageCircle,
  Scale,
  ShieldAlert,
  ShoppingCart,
  X,
  type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import {
  DOC_TYPES,
  MAX_CASE_DETAILS_LENGTH,
  MAX_LOT_NUMBER_LENGTH,
  type CaseRequestType,
  type CreateCaseResponse,
  type DocType,
} from '@/lib/api-types'
import { flags } from '@/lib/flags'
import { CASE_TYPE_LABELS } from '@/lib/status'
import type { FieldErrors } from '@/lib/zod-errors'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldError, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Textarea } from '@/components/ui/textarea'
import { ProductSearch } from '@/components/catalog/product-search'
import { MultiFileDropZone } from '@/components/files/multi-file-drop-zone'
import {
  buildCasePayload,
  emptyRequestForm,
  validateForType,
  type PickedProduct,
  type RequestFormState,
} from './request-schemas'

/**
 * One-page request form: pick what you need (type cards), fill the fields
 * that type actually requires, optionally copy colleagues in and attach
 * files, and send. Validation is the zod mirror of the API schema
 * (request-schemas.ts); the draft autosaves to sessionStorage so a stray
 * navigation loses nothing but the attached files.
 */

const DRAFT_KEY = 'gl:hub:request-draft'

interface TypeCard {
  value: CaseRequestType
  icon: LucideIcon
  description: string
}

const TYPE_CARDS: TypeCard[] = [
  { value: 'document_request', icon: FileText, description: 'COA, TDS, SDS, specs, or statements for a product.' },
  { value: 'questionnaire', icon: ClipboardList, description: 'A supplier or audit questionnaire you need completed.' },
  { value: 'quality_issue', icon: ShieldAlert, description: 'Something wrong with a delivered lot.' },
  { value: 'technical', icon: FlaskConical, description: 'Formulation, usage, or application help.' },
  { value: 'regulatory', icon: Scale, description: 'Compliance, registrations, and market questions.' },
  { value: 'other', icon: MessageCircle, description: 'Anything that doesn’t fit the other cards.' },
]

const SUBMIT_ERROR_COPY: Record<string, string> = {
  validation_failed: 'Something in the request didn’t validate. Check the highlighted fields and resend.',
  too_many_files: 'A request takes up to 5 files — remove some and resend.',
}

interface DraftShape {
  type: CaseRequestType | null
  form: RequestFormState
}

function readDraft(): DraftShape | null {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DraftShape>
    const type = TYPE_CARDS.some((card) => card.value === parsed.type)
      ? (parsed.type as CaseRequestType)
      : null
    // Merge over the empty form so a stale draft shape can't break the page.
    return { type, form: { ...emptyRequestForm(), ...(parsed.form ?? {}) } }
  } catch {
    return null
  }
}

function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    // non-fatal
  }
}

export function RequestCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [draft] = useState(readDraft)
  const [type, setType] = useState<CaseRequestType | null>(draft?.type ?? null)
  const [form, setForm] = useState<RequestFormState>(draft?.form ?? emptyRequestForm)
  const [files, setFiles] = useState<File[]>([])
  const [attempted, setAttempted] = useState(false)
  const [restoredNote, setRestoredNote] = useState(draft !== null)
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [created, setCreated] = useState<CreateCaseResponse | null>(null)

  const accountQuery = useQuery({
    queryKey: ['account'],
    queryFn: ({ signal }) => api.getAccount({ signal }),
    staleTime: 5 * 60_000,
  })
  const colleagues = (accountQuery.data?.colleagues ?? []).filter((c) => !c.isYou)

  const dirty =
    type !== null ||
    form.product !== null ||
    form.description.trim() !== '' ||
    form.subject.trim() !== '' ||
    files.length > 0

  // Draft autosave — files are deliberately excluded (they can't survive
  // sessionStorage), which is also why only attached files warrant a
  // leave-confirmation.
  useEffect(() => {
    if (created) return
    try {
      if (dirty) {
        window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ type, form }))
      }
    } catch {
      // non-fatal
    }
  }, [type, form, dirty, created])

  // A closed tab only loses the attached files — still worth a warning.
  useEffect(() => {
    if (files.length === 0 || created) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [files, created])

  // Errors are DERIVED: once sending was attempted, the zod check re-runs on
  // every keystroke so messages clear as fields are fixed.
  const errors: FieldErrors = attempted && type ? validateForType(type, form) : {}
  const showAlert = attempted && (type === null || Object.keys(errors).length > 0)

  const mutation = useMutation({
    mutationFn: ({ requestType }: { requestType: CaseRequestType }) =>
      api.createCase(buildCasePayload(requestType, form), files, setUploadProgress),
    onSettled: () => setUploadProgress(null),
    onSuccess: (result) => {
      clearDraft()
      setCreated(result)
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
      window.scrollTo({ top: 0 })
    },
    onError: (error: unknown) => {
      const code = error instanceof ApiError ? error.code : 'request_failed'
      setSubmitError(
        SUBMIT_ERROR_COPY[code] ??
          'That didn’t send. Everything you entered is still here — try again.',
      )
      toast.error('The request didn’t send.')
    },
  })

  function patch(changes: Partial<RequestFormState>) {
    setForm((current) => ({ ...current, ...changes }))
  }

  function toggleDocType(doc: DocType) {
    setForm((current) => ({
      ...current,
      docTypes: current.docTypes.includes(doc)
        ? current.docTypes.filter((d) => d !== doc)
        : [...current.docTypes, doc],
    }))
  }

  function toggleParticipant(contactId: string) {
    setForm((current) => ({
      ...current,
      participants: current.participants.includes(contactId)
        ? current.participants.filter((id) => id !== contactId)
        : [...current.participants, contactId],
    }))
  }

  function startFresh() {
    setType(null)
    setForm(emptyRequestForm())
    setFiles([])
    setAttempted(false)
    setRestoredNote(false)
    setSubmitError(null)
    clearDraft()
  }

  function send() {
    if (type === null || Object.keys(validateForType(type, form)).length > 0) {
      setAttempted(true)
      return
    }
    setSubmitError(null)
    mutation.mutate({ requestType: type })
  }

  function handleCancel() {
    // The draft autosaves; only attached files would be lost on leave.
    if (files.length > 0) {
      setConfirmingLeave(true)
      return
    }
    navigate('/requests')
  }

  if (created) {
    return <RequestSuccess result={created} />
  }

  return (
    <div className="mx-auto max-w-form">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/requests"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Requests
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          New request
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title="Start a request"
        description="Tell Green Line what you need — replies land here and in your email."
      />

      {restoredNote ? (
        <div
          role="note"
          className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-paper px-4 py-3"
        >
          <p className="text-sm text-inkMid">
            We kept your draft from earlier — pick up where you left off.
          </p>
          <Button type="button" variant="quiet" size="sm" onClick={startFresh}>
            Start fresh
          </Button>
        </div>
      ) : null}

      <section aria-labelledby="request-type-heading" className="mt-8">
        <SectionHeader id="request-type-heading" title="What do you need?" />
        <div className="mt-4">
          <RadioGroupPrimitive.Root
            value={type ?? ''}
            onValueChange={(next) => setType(next as CaseRequestType)}
            aria-labelledby="request-type-heading"
            className="grid gap-3 sm:grid-cols-2"
          >
            {TYPE_CARDS.map((card) => (
              <RadioGroupPrimitive.Item
                key={card.value}
                value={card.value}
                className={cn(
                  typeCardBaseClasses,
                  'data-[state=checked]:border-brand data-[state=checked]:bg-brandTint/40',
                  'data-[state=unchecked]:border-rule/80 data-[state=unchecked]:hover:border-mute data-[state=unchecked]:hover:bg-cream',
                )}
              >
                <card.icon aria-hidden="true" className="size-5 text-brand" />
                <span className="mt-2.5 block text-sm font-semibold text-ink">
                  {CASE_TYPE_LABELS[card.value]}
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-mute">
                  {card.description}
                </span>
              </RadioGroupPrimitive.Item>
            ))}
            {/* Pricing lives in the Purchasing wizard — the card is a link so
                nobody types a quote request into a service case. */}
            <Link
              to="/purchasing/new"
              className={cn(
                typeCardBaseClasses,
                'border-rule/80 no-underline hover:border-mute hover:bg-cream',
              )}
            >
              <span className="flex items-center justify-between">
                <ShoppingCart aria-hidden="true" className="size-5 text-brand" />
                <ArrowUpRight aria-hidden="true" className="size-4 text-mute" />
              </span>
              <span className="mt-2.5 block text-sm font-semibold text-ink">Pricing &amp; quotes</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-mute">
                {flags.purchasingPricing
                  ? 'Live pack pricing for a purchase order request.'
                  : 'Request an order by pack size — Green Line confirms pricing.'}
              </span>
              <span className="mt-2 block font-mono text-[0.625rem] uppercase tracking-eyebrow text-brandDeep">
                Opens the Purchasing wizard
              </span>
            </Link>
          </RadioGroupPrimitive.Root>
          {attempted && type === null ? (
            <FieldError id="request-type-error">Pick what you need first.</FieldError>
          ) : null}
        </div>
      </section>

      {type ? (
        <section aria-labelledby="request-details-heading" className="mt-10">
          <SectionHeader id="request-details-heading" title="The details" />
          <div className="mt-4 space-y-6">
            <TypeFields
              type={type}
              form={form}
              errors={errors}
              onPatch={patch}
              onToggleDocType={toggleDocType}
            />
          </div>
        </section>
      ) : null}

      {type ? (
        <section aria-labelledby="request-extras-heading" className="mt-10">
          <SectionHeader id="request-extras-heading" title="Sharing &amp; attachments" />
          <div className="mt-4 space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <Field>
                <MicroLabel htmlFor="request-brand">Requesting for a brand?</MicroLabel>
                <Input
                  id="request-brand"
                  value={form.onBehalfOfBrand}
                  onChange={(event) => patch({ onBehalfOfBrand: event.target.value })}
                  maxLength={255}
                  aria-invalid={errors['onBehalfOfBrand'] ? true : undefined}
                  aria-describedby="request-brand-hint"
                />
                <FieldHint id="request-brand-hint">
                  The end brand, if you’re asking on their behalf. Optional.
                </FieldHint>
              </Field>
              <Field>
                <MicroLabel htmlFor="request-reference">Your reference</MicroLabel>
                <Input
                  id="request-reference"
                  value={form.externalReference}
                  onChange={(event) => patch({ externalReference: event.target.value })}
                  maxLength={100}
                  aria-invalid={errors['externalReference'] ? true : undefined}
                  aria-describedby="request-reference-hint"
                />
                <FieldHint id="request-reference-hint">
                  Your own tracking number for this request. Optional.
                </FieldHint>
              </Field>
            </div>

            {colleagues.length > 0 ? (
              <fieldset>
                <legend className="micro-label">Copy colleagues in</legend>
                <p className="mt-1 text-xs leading-relaxed text-mute">
                  They’ll see this request under “Yours” and get its emails.
                </p>
                <div className="mt-3 space-y-2.5">
                  {colleagues.map((colleague) => (
                    <Checkbox
                      key={colleague.id}
                      id={`request-participant-${colleague.id}`}
                      label={colleague.name}
                      description={colleague.email}
                      checked={form.participants.includes(colleague.id)}
                      onChange={() => toggleParticipant(colleague.id)}
                    />
                  ))}
                </div>
              </fieldset>
            ) : null}

            <Field>
              <MicroLabel>Attachments</MicroLabel>
              {type === 'questionnaire' ? (
                <FieldHint>
                  Attach the questionnaire itself — it’s the fastest way to get it completed.
                </FieldHint>
              ) : null}
              {type === 'quality_issue' ? (
                <FieldHint>
                  Photos of the product, packaging, and labels help QC trace the lot faster.
                </FieldHint>
              ) : null}
              <MultiFileDropZone files={files} onChange={setFiles} inputLabel="Attach request files" />
            </Field>
          </div>
        </section>
      ) : null}

      {showAlert ? (
        <p role="alert" className="mt-6 text-sm font-medium text-rust">
          {type === null
            ? 'Pick what you need to get going.'
            : 'Fix the highlighted fields to send your request.'}
        </p>
      ) : null}

      {submitError ? (
        <p role="alert" className="mt-6 rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust">
          {submitError}
        </p>
      ) : null}

      {uploadProgress !== null ? (
        <div
          role="progressbar"
          aria-label="Upload progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(uploadProgress * 100)}
          className="mt-6 h-0.5 w-full overflow-hidden rounded-full bg-rule/40"
        >
          <div className="h-full bg-brand" style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
        </div>
      ) : null}

      {/* Footer controls */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-rule/50 pt-6">
        {confirmingLeave ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-inkMid">
              Your text is saved as a draft, but attached files aren&rsquo;t — leave and they detach.
            </p>
            <Button type="button" variant="neutral" size="sm" onClick={() => navigate('/requests')}>
              Leave anyway
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={() => setConfirmingLeave(false)}>
              Keep going
            </Button>
          </div>
        ) : (
          <Button type="button" variant="quiet" onClick={handleCancel}>
            Cancel
          </Button>
        )}
        <Button type="button" size="lg" loading={mutation.isPending} onClick={send}>
          {mutation.isPending ? 'Sending…' : 'Send request'}
        </Button>
      </div>
    </div>
  )
}

const typeCardBaseClasses = cn(
  'block rounded-lg border bg-card px-4 py-3.5 text-left transition-colors duration-[180ms] ease-editorial',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
)

// ---------------------------------------------------------------------------
// Per-type fields
// ---------------------------------------------------------------------------

function TypeFields({
  type,
  form,
  errors,
  onPatch,
  onToggleDocType,
}: {
  type: CaseRequestType
  form: RequestFormState
  errors: FieldErrors
  onPatch: (changes: Partial<RequestFormState>) => void
  onToggleDocType: (doc: DocType) => void
}) {
  if (type === 'document_request') {
    return (
      <>
        <ProductPickerField
          product={form.product}
          error={errors['productId'] ? 'Pick the product the documents cover.' : undefined}
          onPick={(product) => onPatch({ product })}
          onClear={() => onPatch({ product: null })}
        />
        <Field>
          <MicroLabel htmlFor="request-lot">Lot number</MicroLabel>
          <Input
            id="request-lot"
            value={form.lotNumber}
            onChange={(event) => onPatch({ lotNumber: event.target.value })}
            maxLength={MAX_LOT_NUMBER_LENGTH}
            aria-describedby="request-lot-hint"
          />
          <FieldHint id="request-lot-hint">
            For lot-specific documents like a COA. Optional.
          </FieldHint>
        </Field>
        <fieldset>
          <legend className="micro-label">Documents you need</legend>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2.5">
            {DOC_TYPES.map((doc) => (
              <Checkbox
                key={doc}
                id={`request-doc-${doc}`}
                label={doc}
                checked={form.docTypes.includes(doc)}
                onChange={() => onToggleDocType(doc)}
              />
            ))}
          </div>
          {errors['docTypes'] ? (
            <FieldError id="request-doc-types-error">Tick at least one document.</FieldError>
          ) : null}
        </fieldset>
        <Field>
          <MicroLabel htmlFor="request-details">Anything else?</MicroLabel>
          <Textarea
            id="request-details"
            rows={3}
            value={form.details}
            onChange={(event) => onPatch({ details: event.target.value })}
            maxLength={MAX_CASE_DETAILS_LENGTH}
            aria-describedby="request-details-hint"
          />
          <FieldHint id="request-details-hint">
            Versions, markets, retailer requirements. Optional.
          </FieldHint>
        </Field>
      </>
    )
  }

  if (type === 'questionnaire') {
    return (
      <>
        <Field>
          <MicroLabel htmlFor="request-description">What needs completing?</MicroLabel>
          <Textarea
            id="request-description"
            rows={4}
            value={form.description}
            onChange={(event) => onPatch({ description: event.target.value })}
            aria-invalid={errors['description'] ? true : undefined}
            aria-describedby={
              errors['description'] ? 'request-description-error' : 'request-description-hint'
            }
          />
          {errors['description'] ? (
            <FieldError id="request-description-error">
              Describe the questionnaire and which sections you need.
            </FieldError>
          ) : (
            <FieldHint id="request-description-hint">
              Which sections matter, and any context your team should know.
            </FieldHint>
          )}
        </Field>
        <Field className="sm:max-w-64">
          <MicroLabel htmlFor="request-due">Due date</MicroLabel>
          <Input
            id="request-due"
            type="date"
            value={form.dueDate}
            onChange={(event) => onPatch({ dueDate: event.target.value })}
            aria-describedby="request-due-hint"
          />
          <FieldHint id="request-due-hint">When you need it back. Optional.</FieldHint>
        </Field>
      </>
    )
  }

  if (type === 'quality_issue') {
    return (
      <>
        <ProductPickerField
          product={form.product}
          error={errors['productId'] ? 'Pick the affected product.' : undefined}
          onPick={(product) => onPatch({ product })}
          onClear={() => onPatch({ product: null })}
        />
        <Field>
          <MicroLabel htmlFor="request-lot">Lot number</MicroLabel>
          <Input
            id="request-lot"
            value={form.lotNumber}
            onChange={(event) => onPatch({ lotNumber: event.target.value })}
            maxLength={MAX_LOT_NUMBER_LENGTH}
            aria-invalid={errors['lotNumber'] ? true : undefined}
            aria-describedby={errors['lotNumber'] ? 'request-lot-error' : 'request-lot-hint'}
          />
          {errors['lotNumber'] ? (
            <FieldError id="request-lot-error">
              Add the lot number — it’s on the drum label and the COA.
            </FieldError>
          ) : (
            <FieldHint id="request-lot-hint">On the drum label and the COA.</FieldHint>
          )}
        </Field>
        <Field>
          <MicroLabel htmlFor="request-description">What are you seeing?</MicroLabel>
          <Textarea
            id="request-description"
            rows={4}
            value={form.description}
            onChange={(event) => onPatch({ description: event.target.value })}
            aria-invalid={errors['description'] ? true : undefined}
            aria-describedby={
              errors['description'] ? 'request-description-error' : 'request-description-hint'
            }
          />
          {errors['description'] ? (
            <FieldError id="request-description-error">
              Describe the issue so QC can start tracing it.
            </FieldError>
          ) : (
            <FieldHint id="request-description-hint">
              What’s off, when it arrived, and how it was stored.
            </FieldHint>
          )}
        </Field>
      </>
    )
  }

  // technical | regulatory | other
  return (
    <>
      <Field>
        <MicroLabel htmlFor="request-subject">Subject</MicroLabel>
        <Input
          id="request-subject"
          value={form.subject}
          onChange={(event) => onPatch({ subject: event.target.value })}
          maxLength={255}
          aria-invalid={errors['subject'] ? true : undefined}
          aria-describedby={errors['subject'] ? 'request-subject-error' : undefined}
        />
        {errors['subject'] ? (
          <FieldError id="request-subject-error">Give your question a short subject.</FieldError>
        ) : null}
      </Field>
      <Field>
        <MicroLabel htmlFor="request-description">Your question</MicroLabel>
        <Textarea
          id="request-description"
          rows={4}
          value={form.description}
          onChange={(event) => onPatch({ description: event.target.value })}
          aria-invalid={errors['description'] ? true : undefined}
          aria-describedby={errors['description'] ? 'request-description-error' : undefined}
        />
        {errors['description'] ? (
          <FieldError id="request-description-error">
            Spell out the question so the right person can pick it up.
          </FieldError>
        ) : null}
      </Field>
      <ProductPickerField
        optional
        product={form.product}
        onPick={(product) => onPatch({ product })}
        onClear={() => onPatch({ product: null })}
      />
    </>
  )
}

/**
 * Single-product picker over the shared catalog search: pick one, shown as a
 * removable card. Captures {id, name} only — no pricing rides on a request.
 */
function ProductPickerField({
  product,
  optional = false,
  error,
  onPick,
  onClear,
}: {
  product: PickedProduct | null
  optional?: boolean
  error?: string
  onPick: (product: PickedProduct) => void
  onClear: () => void
}) {
  if (product) {
    return (
      <Field>
        <MicroLabel>Product</MicroLabel>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-brand/40 bg-brandTint/30 px-4 py-3">
          <span className="min-w-0 truncate text-sm font-semibold text-ink">{product.name}</span>
          <button
            type="button"
            onClick={onClear}
            aria-label={`Remove ${product.name}`}
            className="rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
          >
            <X className="size-4" />
          </button>
        </div>
      </Field>
    )
  }

  return (
    <div className="space-y-1.5">
      <ProductSearch
        id="request-product"
        label={optional ? 'Product (optional)' : 'Product'}
        addedIds={new Set<string>()}
        // A request isn't a pricing conversation — the wizard's default
        // no_pricebook copy would read as nonsense here.
        unavailableMessage="Product search isn’t available for your account yet — contact Green Line and we’ll get you set up."
        onAdd={(hit) => onPick({ id: hit.id, name: hit.name })}
      />
      {error ? <FieldError id="request-product-error">{error}</FieldError> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Success terminal
// ---------------------------------------------------------------------------

const FILE_FAILURE_COPY: Record<string, string> = {
  file_type_not_allowed: 'this file type isn’t supported',
  file_too_large: 'over the 10 MB limit',
  upload_failed: 'the upload didn’t finish',
}

function RequestSuccess({ result }: { result: CreateCaseResponse }) {
  const failed = result.files.filter((file) => !file.ok)
  return (
    <div className="mx-auto max-w-2xl py-10 text-center">
      <CircleCheck aria-hidden="true" className="mx-auto size-8 text-brand" />
      <p className="micro-label mt-6">Request sent</p>
      <h1 className="mt-2 font-mono text-3xl font-semibold tracking-tight text-ink">
        #{result.caseNumber}
      </h1>
      <p className="mx-auto mt-4 max-w-sm text-[0.9375rem] leading-relaxed text-inkMid">
        Green Line will pick this up — replies land here and in your email.
      </p>

      {failed.length > 0 ? (
        <div
          role="alert"
          className="mx-auto mt-6 max-w-md rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-left text-sm text-inkMid"
        >
          <p className="font-semibold text-rust">
            {failed.length === 1 ? 'One file didn’t attach' : `${failed.length} files didn’t attach`}
          </p>
          <ul className="mt-2 space-y-1">
            {failed.map((file) => (
              <li key={file.name}>
                <span className="font-mono text-[0.8125rem]">{file.name}</span> —{' '}
                {FILE_FAILURE_COPY[file.error ?? ''] ?? 'it didn’t attach'}. Add it again from the
                request page.
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-8 flex justify-center gap-3">
        <Button asChild>
          <Link to={`/requests/${result.id}`}>View request</Link>
        </Button>
        <Button asChild variant="quiet">
          <Link to="/requests">Back to requests</Link>
        </Button>
      </div>
    </div>
  )
}
